import Vue from "vue";
import User from "@/models/User";
import CourseRun from "@/models/CourseRun";

export default class UserApi {

    constructor(api) {
        this.api = api;
    }

    userId() {
        return this.api.keycloak.subject;
    }

    requireUserId(error) {
        const userId = this.userId();
        if (userId === undefined || userId === null || userId === "undefined" || userId === "") {
            const err = new Error("Cannot call user endpoint before Keycloak subject is available");
            if (error) error(err);
            else Vue.$log.error("Error", err);
            return null;
        }
        return userId;
    }

    get(callback, error) {
        this.getUser(this.userId(), callback, error)
    }

    getUser(userId, callback, error) {
        this.api.getRaw((axios) => {
            axios.get("user/" + userId).then((response) => {
                callback(new User(response.data));
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    updateUser(user, callback, error) {
        this.api.getRaw((axios) => {
            axios.put("user/" + user.id, user).then((response) => {
                callback(new User(response.data));
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    createUser(user, callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user", user).then((response) => {
                callback(new User(response.data));
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    acceptDataProtection(callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + this.userId() + "/acceptDataProtection")
        }, error)
    }

    promoteUser(userId, callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/setAdmin").then(() => {
                callback()
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            })
        }, error)

    }

    degradeUser(userId, callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/removeAdmin").then(() => {
                callback()
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            })
        }, error)
    }

    resetPassword(userId, callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/passwordReset").then(() => {
                callback();
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    setTemporaryPassword(userId, password, callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/passwordReset/" + password).then(() => {
                callback();
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    companyUsers(withInactive, callback, error) {
        this.api.getRaw((axios) => {
            axios.get("user?withInactive=" + withInactive).then((response) => {
                callback(response.data.map((date) => new User(date)));
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    companyUsersWithSearchTerm(searchTerm, withInactive, callback, error) {
        this.api.getRaw((axios) => {
            axios.get("user?searchTerm=" + searchTerm + "&withInactive=" + withInactive).then((response) => {
                callback(response.data.map((date) => new User(date)));
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    companyUsersForCompany(withInactive, company, callback, error) {
        this.api.getRaw((axios) => {
            axios.get("user?withInactive=" + withInactive + "&companyId=" + company).then((response) => {
                callback(response.data.map((date) => new User(date)));
            }).catch((err) => {
                if (error) error(err)
                else Vue.$log.error("Error", error);
            });
        });
    }

    trackProgress(courseRun, success, error) {
        const userId = this.requireUserId(error);
        if (userId === null) return;

        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/courseProgress", courseRun, {
                timeout: 15000,
            })
                .then((response) => {
                    if (success) success(response.data);
                })
                .catch((err) => {
                    if (error) error(err);
                    console.error(err);
                });
        });
    }

    trackProgressForPart(part, success, error) {
        const userId = this.requireUserId(error);
        if (userId === null) return;

        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/courseProgressCompound", part, {
                timeout: 15000,
            })
                .then((response) => {
                    if (success) success(response.data);
                })
                .catch((err) => {
                    if (error) error(err);
                    console.error(err);
                });
        });
    }

    reportCourseSyncIssue(issue, success, error) {
        const userId = this.requireUserId(error);
        if (userId === null) return;

        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/courseSyncIssue", issue, {
                timeout: 10000,
            })
                .then((response) => {
                    if (success) success(response.data);
                })
                .catch((err) => {
                    if (error) error(err);
                    console.error(err);
                });
        });
    }

    submitShippersDeclaration(declaration, courseRunId, partId, success, error) {
        const formData = new FormData();
        formData.append("file", declaration);
        this.api.getRaw((axios) => {
            axios.post("user/" + this.userId() + "/shippersdeclaration/" + courseRunId + "/" + partId, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            }, () => {
            }).then(() => {
                success();
            }).catch((err) => {
                Vue.$log.error("Error", err);
                error();
            });
        });
    }

    startCourseRun(courseRunId, success, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + this.userId() + "/courseStart/" + courseRunId).then((response) => {
                success(new CourseRun(response.data));
            }).catch((err) => {
                Vue.$log.error("Error", err);
                error();
            });
        });
    }

    submitDeclaredGoods(declaration, courseRunId, partId, success, error) {
        const formData = new FormData();
        formData.append("file", declaration);
        this.api.getRaw((axios) => {
            axios.post("user/" + this.userId() + "/declaredgoods/" + courseRunId + "/" + partId, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            }, () => {
            }).then(() => {
                success();
            }).catch((err) => {
                Vue.$log.error("Error", err);
                error();
            });
        });
    }

    resetCourseProgress(userId, courseRunId, callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/courseReset/" + courseRunId)
                .then(() => {
                    callback();
                })
                .catch((err) => {
                    if (error) error(err)
                });
        });
    }

    finishCourseProgress(userId, courseRunId, callback, error) {
        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/courseFinish/" + courseRunId).then(() => {
                callback();
            }).catch((err) => {
                if (error) error(err)
            });
        });
    }

    clearCourseResumeState(userId, courseRunId, partId, callback, error) {
        const query = partId ? "?partId=" + encodeURIComponent(partId) : "";
        this.api.getRaw((axios) => {
            axios.post("user/" + userId + "/courseResumeStateClear/" + courseRunId + query).then((response) => {
                if (callback) callback(new User(response.data));
            }).catch((err) => {
                if (error) error(err)
            });
        });
    }

}
